import { getIdeyaBaseUrl } from '../api.util.ts'
import {
  BaseQueryApi,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
} from '@reduxjs/toolkit/query/react'
import { getLocalStorageItem } from '../../utils/local-storage.ts'
import {
  Auth0TokenExchangeResponse,
  GetUserInfoResponse,
  LoginResponse,
} from './auth.def.ts'
import { staticNotification as notification } from '../../common/antd/static-notification.ts'

const interfaceName = 'auth'

function getBaseUrl(): string {
  const endpoint = getIdeyaBaseUrl()
  return endpoint + interfaceName
}

async function customBaseQuery(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object
  //extraOptions: { userToken?: string } = {}
): Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>> {
  const baseQuery = fetchBaseQuery({
    baseUrl: getBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers) => {
      if (!headers.has('Authorization')) {
        const token = getLocalStorageItem('apiAccessToken')
        if (token) {
          headers.set('Authorization', `Bearer ${token}`)
        }
      }
      headers.set('Content-Type', 'application/json')
      return headers
    },
  })

  const result = await baseQuery(args, api, extraOptions)
  if (result.error) {
    const url = typeof args === 'string' ? args : args.url
    const data = result.error.data as { message?: string; errorCode?: string }
    const message = data?.message as string

    // The /verify probe fired before login expectedly fails with an auth error
    // (401/403 or InvalidAuthToken). That is a normal "not logged in" signal, so
    // suppress the notification for it; all other errors still notify.
    const isVerify = url?.includes('/verify')
    const isExpectedAuthFailure =
      result.error.status === 401 ||
      result.error.status === 403 ||
      data?.errorCode === 'InvalidAuthToken'

    if (!(isVerify && isExpectedAuthFailure)) {
      notification.error({
        message: `Error: ${result.error.status}`,
        description: message ? message : 'An error occurred',
      })
    }
  }
  return result
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: customBaseQuery,
  tagTypes: ['Auth'],
  endpoints: (build) => ({
    login: build.query<LoginResponse, void>({
      query: () => ({
        url: '/login',
        method: 'POST',
      }),
    }),
    getUserInfo: build.query<GetUserInfoResponse, void>({
      query: () => ({
        url: '/verify',
        method: 'GET',
      }),
      providesTags: ['Auth'],
    }),
    auth0ExchangeToken: build.query<Auth0TokenExchangeResponse, string>({
      query: (userToken) => {
        return {
          url: '/login/auth0',
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + userToken,
          },
        }
      },
    }),
  }),
})

export const {
  useLazyLoginQuery,
  useLazyGetUserInfoQuery,
  useAuth0ExchangeTokenQuery,
} = authApi
